import { CopyResponse, fileCard, formatUsd, LocationDetail, LocationList, OkResponse, type Copy, type Location } from '@mtg/shared';
import { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { api, errorText } from '../api.ts';
import { CardGroups, type Item } from '../components/CardGroups.tsx';
import { DeckInsightsPanel } from '../components/DeckInsights.tsx';
import { DeckView } from '../components/DeckView.tsx';
import { plural } from '../format.ts';
import { useApi } from '../useApi.ts';

export function LocationPage({ id }: { id: string }) {
  const detail = useApi(() => api(`/api/locations/${id}`, LocationDetail), [id]);
  const locations = useApi(() => api('/api/locations', LocationList), []);
  const [error, setError] = useState<string | null>(null);
  const [, navigate] = useLocation();

  if (detail.error) return <p className="error">{detail.error}</p>;
  if (!detail.data) return <p className="muted">Loading…</p>;
  const { location, deck, copies } = detail.data;
  const byId = new Map(copies.map((c) => [c.id, c]));
  const items: Item[] = copies.map((c) => ({ key: c.id, card: c.card, finish: c.finish, quantity: c.quantity, lang: c.lang, condition: c.condition }));

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      detail.reload();
    } catch (e) {
      setError(errorText(e));
    }
  }

  const move = (copy: Copy, to: string, count: number) =>
    run(() => api(`/api/copies/${copy.id}`, CopyResponse, { method: 'PATCH', body: { locationId: to, count } }));
  const removeOne = (copy: Copy) => run(() => api(`/api/copies/${copy.id}?count=1`, OkResponse, { method: 'DELETE' }));
  const updateDeck = (changes: object) => run(() => api(`/api/decks/${id}`, LocationDetail, { method: 'PATCH', body: changes }));
  const deleteDeck = async () => {
    if (!confirm(`Delete the deck "${location.name}"?`)) return;
    setError(null);
    try {
      await api(`/api/decks/${id}`, OkResponse, { method: 'DELETE' });
      navigate('/');
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="stack">
      <p className="small">
        <Link href="/">← Collection</Link>
      </p>
      <h1>
        {location.name}
        {location.label && <span className="muted"> · {location.label}</span>}
      </h1>
      <p className="lead">
        {plural(location.cardCount, 'card')} · {formatUsd(location.value)}
      </p>
      {deck && <DeckControls deck={deck} count={location.cardCount} onChange={updateDeck} onDelete={() => void deleteDeck()} />}
      {deck && copies.length > 0 && <DeckInsightsPanel deckId={location.id} />}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {copies.length === 0 ? (
        <p className="muted">
          Nothing here yet. <Link href="/add">Add a card</Link>.
        </p>
      ) : deck ? (
        <DeckView
          items={items}
          commanders={deck.commanders}
          actions={(item) => {
            const copy = byId.get(item.key)!;
            const isCommander = deck.commanders.includes(copy.card.scryfallId);
            return (
              <div className="tile-actions">
                <MoveControl copy={copy} here={location} locations={locations.data?.locations ?? []} onMove={move} />
                <div className="row tight">
                  <button type="button" className="btn small" onClick={() => void removeOne(copy)}>
                    Remove 1
                  </button>
                  <button
                    type="button"
                    className="btn small"
                    onClick={() =>
                      void updateDeck({
                        commanders: isCommander
                          ? deck.commanders.filter((c) => c !== copy.card.scryfallId)
                          : [...deck.commanders, copy.card.scryfallId].slice(-2),
                      })
                    }
                  >
                    {isCommander ? 'Not commander' : 'Make commander'}
                  </button>
                </div>
              </div>
            );
          }}
        />
      ) : (
        <CardGroups
          items={items}
          drawerId={location.kind === 'drawer' ? location.id : undefined}
          actions={(item) => {
            const copy = byId.get(item.key)!;
            // Offer to file a card that isn't in its drawer.
            const spot = fileCard(copy.card);
            const fileTo = spot.drawerId && spot.drawerId !== location.id ? spot : null;
            const fileName = fileTo && locations.data?.locations.find((l) => l.id === fileTo.drawerId);
            return (
              <div className="tile-actions">
                {fileTo && fileName && (
                  <button type="button" className="btn small primary" onClick={() => void move(copy, fileTo.drawerId, copy.quantity)}>
                    File: {fileName.name.replace('Catalog ', 'C').replace(' / Drawer ', ' D')} · {fileTo.divider}
                  </button>
                )}
                <MoveControl copy={copy} here={location} locations={locations.data?.locations ?? []} onMove={move} />
                <div className="row tight">
                  <button type="button" className="btn small" onClick={() => void removeOne(copy)}>
                    Remove 1
                  </button>
                </div>
              </div>
            );
          }}
        />
      )}
    </div>
  );
}

function MoveControl({
  copy,
  here,
  locations,
  onMove,
}: {
  copy: Copy;
  here: Location;
  locations: Location[];
  onMove: (copy: Copy, to: string, count: number) => void;
}) {
  const [count, setCount] = useState(1);
  return (
    <div className="row tight">
      {copy.quantity > 1 && (
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} aria-label="How many to move">
          {Array.from({ length: copy.quantity }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      )}
      <select
        value=""
        onChange={(e) => e.target.value && onMove(copy, e.target.value, Math.min(count, copy.quantity))}
        aria-label={`Move ${copy.card.name} to`}
      >
        <option value="">Move to…</option>
        {locations
          .filter((l) => l.id !== here.id)
          .map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
              {l.label ? ` · ${l.label}` : ''}
            </option>
          ))}
      </select>
    </div>
  );
}

function DeckControls({
  deck,
  count,
  onChange,
  onDelete,
}: {
  deck: NonNullable<LocationDetail['deck']>;
  count: number;
  onChange: (changes: object) => void;
  onDelete: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const shareUrl = deck.shareSlug ? `${location.origin}/d/${deck.shareSlug}` : null;
  return (
    <section className="panel stack">
      <p>
        <strong>{deck.format}</strong> · {plural(count, 'card')}
        {deck.format === 'commander' && count !== 100 && <span className="warn"> (a Commander deck has 100)</span>}
        {deck.commanders.length === 0 && <span className="muted"> · No commander set: use “Commander” on a card.</span>}
      </p>
      {shareUrl ? (
        <div className="row">
          <input readOnly value={shareUrl} aria-label="Share link" onFocus={(e) => e.target.select()} />
          <button
            type="button"
            className="btn"
            onClick={() => void navigator.clipboard.writeText(shareUrl).then(() => setCopied(true))}
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button type="button" className="btn" onClick={() => onChange({ shared: false })}>
            Stop sharing
          </button>
        </div>
      ) : (
        <div className="row">
          <button type="button" className="btn" onClick={() => onChange({ shared: true })}>
            Share with friends
          </button>
          <span className="muted small">Makes a read-only page anyone with the link can open.</span>
        </div>
      )}
      <GalleryPlacement deck={deck} onChange={onChange} />
      {count === 0 && (
        <div className="row">
          <button type="button" className="btn small danger" onClick={onDelete}>
            Delete deck
          </button>
        </div>
      )}
    </section>
  );
}

/** Where the deck sits on the shared /decks gallery: a section, its place in it, and a label. */
function GalleryPlacement({ deck, onChange }: { deck: NonNullable<LocationDetail['deck']>; onChange: (changes: object) => void }) {
  const [section, setSection] = useState(deck.gallerySection);
  const [order, setOrder] = useState(deck.galleryOrder === null ? '' : String(deck.galleryOrder));
  const [label, setLabel] = useState(deck.galleryLabel);
  const changed = section !== deck.gallerySection || order !== (deck.galleryOrder === null ? '' : String(deck.galleryOrder)) || label !== deck.galleryLabel;
  return (
    <details className="gallery-placement">
      <summary>
        On the shared decks page:{' '}
        <span className="muted">
          {deck.gallerySection ? `${deck.gallerySection}${deck.galleryLabel ? ` · ${deck.galleryLabel}` : ''}` : 'Other decks'}
        </span>
      </summary>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          onChange({ gallerySection: section.trim(), galleryOrder: order.trim() === '' ? null : Number(order), galleryLabel: label.trim() });
        }}
      >
        <label className="field">
          <span>Section</span>
          <input value={section} onChange={(e) => setSection(e.target.value)} placeholder="e.g. Final Fantasy" maxLength={100} />
        </label>
        <label className="field">
          <span>Order</span>
          <input type="number" min={0} max={9999} value={order} onChange={(e) => setOrder(e.target.value)} />
        </label>
        <label className="field">
          <span>Label</span>
          <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. FF VI" maxLength={60} />
        </label>
        <button type="submit" className="btn" disabled={!changed}>
          Save
        </button>
      </form>
    </details>
  );
}
